import React, { useState } from 'react';
import { ListPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button, Modal, DateInput } from '../../common';
import { SearchableSelect } from '../../common/SearchableSelect';
import UserSelect from './UserSelect';
import { createActivityForProject } from '../../../services/dossier.service';
import type { DossierActivity } from '../../../types';

type ActivityStatus = DossierActivity['status'];

interface Props {
  projectId: number;
  attachmentId: number;
  fileName?: string;
  docTitle?: string;
  // Notifică părintele după creare (ex: reîncarcă lista de activități corelate).
  onCreated?: () => void;
  size?: 'sm' | 'md';
  variant?: 'icon' | 'button';
}

const STATUS_OPTIONS: { value: ActivityStatus; label: string }[] = [
  { value: 'pending', label: 'De făcut' },
  { value: 'in_progress', label: 'În lucru' },
  { value: 'completed', label: 'Finalizat' },
];

const LABEL = 'Adaugă activitate';
const TOOLTIP = 'Adaugă activitate pentru acest document';

// Numele implicit al activității: titlul documentului sau numele fișierului fără extensie.
// Extensia se taie doar dacă e una scurtă și reală (evită trunchierea la „Aviz nr. 12.3 mediu").
function defaultActivityName(docTitle?: string, fileName?: string): string {
  if (docTitle && docTitle.trim()) return docTitle.trim();
  const raw = (fileName || '').trim();
  if (!raw) return '';
  return raw.replace(/\.[A-Za-z0-9]{1,8}$/, '') || raw;
}

const EMPTY_FORM = {
  name: '',
  description: '',
  status: 'pending' as ActivityStatus,
  responsible_user_id: null as number | null,
  start_date: '',
  due_date: '',
};

export default function AddActivityForDocumentButton({
  projectId,
  attachmentId,
  fileName,
  docTitle,
  onCreated,
  size = 'sm',
  variant = 'button',
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  // Fără context de proiect (sau de document) nu putem crea corelarea — nu randăm nimic.
  if (!Number.isFinite(projectId) || projectId <= 0) return null;
  if (!Number.isFinite(attachmentId) || attachmentId <= 0) return null;

  const inputClass =
    'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

  const displayName = docTitle || fileName || 'documentul selectat';

  const openModal = () => {
    // Re-prepopulăm la fiecare deschidere: documentul se poate schimba între randări.
    setForm({ ...EMPTY_FORM, name: defaultActivityName(docTitle, fileName) });
    setIsOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setIsOpen(false);
    setForm({ ...EMPTY_FORM });
  };

  const handleSubmit = async () => {
    const name = form.name.trim();
    if (!name) return;
    setSaving(true);
    try {
      await createActivityForProject(projectId, {
        name,
        description: form.description.trim() || undefined,
        status: form.status,
        responsible_user_id: form.responsible_user_id ?? undefined,
        start_date: form.start_date || undefined,
        due_date: form.due_date || undefined,
        attachment_id: attachmentId,
      });
      setIsOpen(false);
      setForm({ ...EMPTY_FORM });
      toast.success('Activitate creată și corelată cu documentul.');
      onCreated?.();
    } catch (err) {
      // Eroare: păstrăm modalul deschis, ca datele completate să nu se piardă.
      console.error(err);
      // Pe 401/403/404 mesajul din ApiError e generic, dar corpul serverului rămâne
      // în `details` — de acolo luăm motivul real (ex. proiect fără dosar inițializat),
      // altfel utilizatorul primește „încearcă din nou" pentru ceva ce reîncercarea nu repară.
      const e = err as { message?: string; details?: { error?: string; message?: string } };
      const serverMsg = e?.details?.error || e?.details?.message;
      toast.error(serverMsg || e?.message || 'Activitatea nu a putut fi creată. Încearcă din nou.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {variant === 'icon' ? (
        <button
          type="button"
          onClick={openModal}
          title={TOOLTIP}
          aria-label={TOOLTIP}
          className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <ListPlus className={size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'} />
        </button>
      ) : (
        <Button
          variant="ghost"
          size={size}
          onClick={openModal}
          title={TOOLTIP}
          leftIcon={<ListPlus className={size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'} />}
        >
          {LABEL}
        </Button>
      )}

      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        title="Activitate nouă pentru document"
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={closeModal} disabled={saving}>
              Anulează
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={handleSubmit}
              disabled={saving || !form.name.trim()}
              isLoading={saving}
            >
              {saving ? 'Se creează...' : 'Creează'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-800">
            <ListPlus className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              Activitatea va fi corelată cu documentul:{' '}
              <span className="font-medium break-all">{displayName}</span>
            </span>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Denumire activitate *</label>
            <input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="ex: Verificare aviz de mediu"
              className={inputClass}
              disabled={saving}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descriere</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={3}
              placeholder="Detalii (opțional)"
              className={inputClass}
              disabled={saving}
            />
          </div>

          {/* Responsabil opțional: dacă lipsește, backend-ul atribuie activitatea creatorului. */}
          <UserSelect
            projectId={projectId}
            value={form.responsible_user_id}
            onChange={userId => setForm(f => ({ ...f, responsible_user_id: userId }))}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <SearchableSelect
              options={STATUS_OPTIONS.map(opt => ({ value: opt.value, label: opt.label }))}
              value={form.status}
              onChange={v => setForm(f => ({ ...f, status: v as ActivityStatus }))}
              disabled={saving}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <DateInput
              label="Dată început"
              value={form.start_date}
              onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
              disabled={saving}
            />
            <DateInput
              label="Termen"
              value={form.due_date}
              onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
              disabled={saving}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}
